import { Epic } from 'redux-observable'
import { AjaxResponse } from 'rxjs/ajax'
import { delay, exhaustMap, filter, groupBy, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { addDays, format, subDays } from 'date-fns'
import { EpicDependencies, RootState } from 'src/state/types'
import { BeneficiaryType } from 'types/finance'
import { ENDPOINTS } from './constants'
import { events, ReconEvent } from './state'
import * as selectors from './selectors'
import * as transformers from './transformers'
import { events as uiInvoiceEvents, UiInvoiceEvent } from '../../ui/invoices/state'
import { multiUserOperators } from '../../multiUser/state'
import { bankingApiConstants, bankingApiEvents } from '../banking'
import { BankingEvent } from '../banking/state'
import { TPartiesErrorsResponse } from '../banking/types'
import { invoiceApiTransformers } from '../invoice'
import { depositInvoiceTypes } from '../invoice/constants'
import { TBulkInvoicesResponse } from '../invoice/types'
import { reconApiSelectors } from '.'
import {
  TApplicationInvoiceStatus,
  TBulkInvoiceUploadError,
  TCustomerAccountActivity,
  TEasyPayData,
  TOpenInvoiceSummary,
  TReconBeneficiaryRule,
  TReconInvoice,
  TReconNotification,
} from './types'

type ReconEpic = Epic<ReconEvent, ReconEvent, RootState, EpicDependencies>

export const apiFetchInvoices: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoices_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_INVOICES_V2, state$, action.payload).pipe(
        pluck('response'),
        map((res: TReconInvoice[]) => events.invoices_success(transformers.transformInvoiceResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiFetchInvoices'),
  )

export const apiFetchInvoice: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoice_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_INVOICE, state$, action.payload).pipe(
        pluck('response'),
        map((res: TReconInvoice) => events.invoice_success(transformers.transformInvoiceResponse(res))),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiFetchInvoice'),
  )

export const apiFetchCreditNoteReasons: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.creditNoteReasons_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.FETCH_CREDIT_NOTE_REASONS, state$, {}).pipe(
        pluck('response', 'creditNoteReasonSummaries'),
        map(events.creditNoteReasons_success),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiFetchCreditNoteReasons'),
  )

export const apiCreditNoteInvoice: ReconEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.creditNote_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.CREDIT_NOTE_INVOICE, state$, transformers.transformCreditNoteRequest(body), params).pipe(
        pluck('response'),
        map((res: TReconInvoice[]) =>
          events.creditNote_success(transformers.transformInvoiceResponse(res), {
            id: params.id,
            creditNoteAmount: body.amount,
          }),
        ),
        catchRestError(action, false),
      )
    }),
    tag('recon/epics/apiCreditNoteInvoice'),
  )

export const apiUpdateDepositBeneficiaryOnCreditNoteSuccess: ReconEpic = (action$, state$) =>
  action$.pipe(
    filter(isOfType(events.creditNote_success.type)),
    mergeMap(({ meta }) => {
      // find invoice using selector and invoice ID from credit success payload
      const invoice = reconApiSelectors.getInvoiceById(state$.value)(meta.id)

      // check if it's a credit note for a deposit invoice
      if (!invoice || !depositInvoiceTypes.includes(invoice.invoiceType)) {
        return []
      }

      const beneficiaryAmount = invoice.beneficiaries[0]?.beneficiary?.value?.amount || 0

      /** Calculate the new amount */
      const newAmount = beneficiaryAmount - meta.creditNoteAmount

      // update deposit beneficiary
      const beneficiaries = invoice.beneficiaries.map(
        b =>
          ({
            ...b,
            beneficiary: {
              ...b.beneficiary,
              value: {
                ...b.beneficiary.value,
                amount: newAmount,
              },
            },
          } as TReconBeneficiaryRule),
      )

      return [
        events.updateBenficiaries_request({
          body: beneficiaries,
          params: { invoiceId: meta.id },
        }),
      ]
    }),
    tag('recon/epics/apiUpdateDepositBeneficiaryOnCreditNoteSuccess'),
  )

export const apiCreatePayment: ReconEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.payment_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return post(ENDPOINTS.CREATE_PAYMENT_V2, state$, transformers.transformCreatePaymentRequest(body), params).pipe(
        pluck('response'),
        map((res: TReconInvoice[]) => events.payment_success(transformers.transformInvoiceResponse(res))),
        catchRestError(action),
      )
    }),
    tag('recon/epics/apiCreatePayment'),
  )

export const apiUpdateBeneficiaries: ReconEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateBenficiaries_request.type)),
    mergeMap(action => {
      const { body, params } = action.payload
      return put(
        ENDPOINTS.UPDATE_BENEFICIARIES_V2,
        state$,
        transformers.transformUpdateBeneficiariesRequest(body),
        params,
      ).pipe(
        pluck('response'),
        map(res =>
          events.updateBenficiaries_success(transformers.transformUpdateBeneficiariesResponse(res), {
            invoiceId: params.invoiceId,
          }),
        ),
        catchRestError(action),
      )
    }),
    tag('recon/epics/apiUpdateBeneficiaries'),
  )

export const apiNudge: ReconEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoicesNudge_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.NUDGE, state$, action.payload, {}).pipe(
        pluck('response'),
        map(events.invoicesNudge_success),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiNudge'),
  )

export const apiFetchNotifications: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.notifications_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action =>
      get(ENDPOINTS.NOTIFICATIONS, state$, {}).pipe(
        pluck('response'),
        map((res: TReconNotification[]) => events.notifications_success(res)),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiFetchNotifications'),
  )

export const apiFetchCustomerActiveInvoices: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.customerActiveInvoices_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.CUSTOMER_OPEN_INVOICES, state$, action.payload).pipe(
        pluck('response'),
        map((response: TOpenInvoiceSummary[]) =>
          events.customerActiveInvoices_success(transformers.transformActiveInvoices(response), action.payload),
        ),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiFetchCustomerActiveInvoices'),
  )

export const apiFetchCustomerAccountActivity: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.customerAccountActivity_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.CUSTOMER_ACCOUNT_ACTIVITY, state$, action.payload).pipe(
        pluck('response'),
        map((response: TCustomerAccountActivity) =>
          events.customerAccountActivity_success(
            transformers.transformCustomerAccountActivity(response),
            action.payload,
          ),
        ),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiFetchCustomerAccountActivity'),
  )

export const bulkActionApprovePayments: Epic<
  ReconEvent | UiInvoiceEvent | BankingEvent,
  ReconEvent | BankingEvent,
  RootState,
  EpicDependencies
> = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(uiInvoiceEvents.bulkActionApprovePayments.type)),
    delay(500), // delay for animation
    pluck('payload'),
    mergeMap((invoiceIds: string[]) => {
      const invoiceData = invoiceIds
        .map(invoiceId => ({
          invoiceId,
          // Already-settled beneficiaries are left out by the selector: they have nothing
          // outstanding, and posting a payment against one rejects the whole allocation.
          beneficiaries: selectors.getInvoicePaymentBeneficiaries(state$.value)(invoiceId),
        }))
        // Nothing left to pay on this invoice (no beneficiaries, or every one is settled).
        .filter(({ beneficiaries }) => beneficiaries.length > 0)

      // Deposit beneficiaries are internal deposit transfers and do not require bank
      // details, so exclude them from the banking bank-details validation check.
      const requiresBankDetails = (b: { type?: BeneficiaryType }) => b.type !== BeneficiaryType.DepositBeneficiary

      const allPartyIds = Array.from(
        new Set(
          invoiceData
            .flatMap(({ beneficiaries }) => beneficiaries.filter(requiresBankDetails).map(b => b.partyId))
            .filter((id): id is string => Boolean(id)),
        ),
      )

      const buildPaymentActions = (errorPartyIds: Set<string>) =>
        invoiceData
          .map(({ invoiceId, beneficiaries }) => ({
            invoiceId,
            body: beneficiaries
              // Deposit beneficiaries are always payable; only gate the others on
              // banking errors so a missing-bank-details party can't block a deposit.
              .filter(b => !requiresBankDetails(b) || !b.partyId || !errorPartyIds.has(b.partyId))
              .map(({ beneficiaryId, amount }) => ({ beneficiaryId, amount })),
          }))
          .filter(({ body }) => body.length > 0)
          .map(({ invoiceId, body }) => events.payment_request({ body, params: { invoiceId } }))

      // No parties require bank-details validation (e.g. deposit-only approvals), so
      // skip the banking call and approve directly.
      if (allPartyIds.length === 0) {
        return buildPaymentActions(new Set<string>())
      }

      const partyIdsParam = allPartyIds.join(',')
      const payload = { party_ids: partyIdsParam }

      return get(bankingApiConstants.ENDPOINTS.FETCH_PARTIES_ERRORS, state$, payload)
        .pipe(
          pluck('response'),
          map((response: TPartiesErrorsResponse | null) =>
            bankingApiEvents.partiesErrors_success({ response, params: payload }),
          ),
          catchRestError(bankingApiEvents.partiesErrors_request({ party_ids: partyIdsParam })),
        )
        .pipe(
          mergeMap(action => {
            const response = action.payload?.response as Record<string, unknown> | null | undefined
            const errorPartyIds = new Set<string>(response ? Object.keys(response) : [])

            return [action, ...buildPaymentActions(errorPartyIds)]
          }),
        )
    }),
    tag('recon/epic/bulkActionApprovePayments'),
  )

export const bulkActionNudgeInvoices: Epic<
  UiInvoiceEvent | ReconEvent,
  ReconEvent,
  RootState,
  EpicDependencies
> = action$ =>
  action$.pipe(
    filter(isOfType(uiInvoiceEvents.bulkActionNudgeInvoices.type)),
    delay(500), // delay for animation
    pluck('payload'),
    map((invoiceIds: string[]) => events.invoicesNudge_request({ invoiceIds })),
    tag('recon/epic/bulkActionNudgeInvoices'),
  )

export const apiFetchEasyPayData: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.easypayData_request.type)),
    filter(action => {
      const cached = reconApiSelectors.getEasyPayDataByReference(state$.value)(action.payload.reference)
      return !cached.request?.success
    }),
    groupBy(action => action.payload.reference),
    mergeMap(group$ =>
      group$.pipe(
        exhaustMap(action => {
          const { reference } = action.payload
          return get(`${ENDPOINTS.EASYPAY}?reference=${reference}`, state$, {}).pipe(
            pluck('response'),
            map((res: TEasyPayData) => events.easypayData_success(res, { reference })),
            catchRestError(action),
          )
        }),
      ),
    ),
    tag('recon/epics/apiFetchEasyPayData'),
  )

export const fetchActiveInvoices: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.invoicesActive_request.type)),
    mergeMap(action => {
      const { id } = action.payload
      return get(ENDPOINTS.FETCH_ACTIVE_INVOICES, state$, { id }).pipe(
        pluck('response'),
        map((res: { invoices?: string[] } | null) =>
          events.invoicesActive_success({ activeInvoices: res?.invoices ?? [], id }),
        ),
        catchRestError(action),
      )
    }),
    tag('recon/epics/fetchActiveInvoices'),
  )

export const apiBulkInvoiceUpload: ReconEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.bulkInvoiceUpload_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.BULK_INVOICE_UPLOAD, state$, action.payload, {}).pipe(
        pluck('response'),
        map((res: (TBulkInvoicesResponse & { errors?: TBulkInvoiceUploadError[] }) | null) =>
          res?.errors
            ? events.bulkInvoiceUpload_failed(res.errors)
            : events.bulkInvoiceUpload_success(invoiceApiTransformers.transformGetBulkInvoicesResponse(res)),
        ),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/apiBulkInvoiceUpload'),
  )

export const fetchApplicationInvoiceStatusByPortfolioId: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.applicationInvoiceByPortfolio_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.APPLICATION_INVOICES_STATUS, state$, action.payload).pipe(
        pluck('response'),
        map((res: TApplicationInvoiceStatus[]) => events.applicationInvoiceByPortfolio_success(res, action.payload)),
        catchRestError(action),
      ),
    ),
    tag('recon/epics/fetchApplicationInvoiceStatusByPortfolioId'),
  )

export const apiFetchArchived: ReconEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.archived_request.type)),
    mergeMap(action => {
      const { startDate, endDate } = action.payload
      const startDateMinusOneDay = format(subDays(new Date(startDate), 1), 'yyyy-MM-dd')
      const endDatePlusOneDay = format(addDays(new Date(endDate), 1), 'yyyy-MM-dd')
      return get(ENDPOINTS.ARCHIVED_V2, state$, { startDate: startDateMinusOneDay, endDate: endDatePlusOneDay }).pipe(
        pluck('response'),
        map((res: TReconInvoice[]) =>
          events.archived_success(transformers.transformInvoiceResponse(res), { startDate, endDate }),
        ),
        catchRestError(action),
      )
    }),
    tag('recon/epics/apiFetchArchived'),
  )
